/**
 * libraries.js
 * 
 * A library is the list of things being auctioned (movies, footballers, anything).
 * 
 * Shape (same for built-in libraries, libraries from the database, and the copy stored on a room):
 *  {
 *      code            :   'bollywood',            // ID people type in to use it
 *      name, description,
 *      nameAttribute   :   'Movie',                // Column that names each item
 *      scoreAttribute  :   'Rating',               // Column teams are judged on (Rating / Points / Marks)
 *      attributes      :   [{ name, kind }],       // kind: 'name' | 'score' | 'category' | 'number' | 'text'
 *      items           :   [{ name, score, values: { [attribute]: value } }],
 *      itemCount,
 *  }
 * 
 * Only 'category' attributes (values that repeat, like Actor or Genre) get constraints and bonuses.
 */

import { movieList } from "../assets/movies";
import { getPosterImage } from "../helpers/getPosterOfMovie";

export const SCORE_ATTRIBUTE_PATTERN = /^(rating|points|marks)$/i;

export const BOLLYWOOD_CODE = "bollywood";

/**
 * The original Bollywood movies list, built into the app
 */
export const bollywoodLibrary = {
    code            :   BOLLYWOOD_CODE,
    name            :   "Bollywood Movies",
    description     :   "Shah Rukh, Salman, Aamir and Hrithik, across romance, drama, action and inspiration.",
    nameAttribute   :   "Movie",
    scoreAttribute  :   "Rating",
    attributes      :   [
        { name: "Movie",  kind: "name"     },
        { name: "Actor",  kind: "category" },
        { name: "Genre",  kind: "category" },
        { name: "Rating", kind: "score"    },
    ],
    items           :   movieList.map((movie) => ({
        name    :   movie.movieName,
        score   :   movie.rating,
        values  :   { Actor: movie.actor, Genre: movie.genre },
    })),
    itemCount       :   movieList.length,
    // The original rules of the game
    defaultRules    :   {
        Actor   :   { minDifferent: 4, maxPerValue: 2,    bonusOneValue: 0,  bonusEveryValue: 10 },
        Genre   :   { minDifferent: 0, maxPerValue: null, bonusOneValue: 15, bonusEveryValue: 10 },
    },
};

export const builtInLibraries = [bollywoodLibrary];

const emptyRule = { minDifferent: 0, maxPerValue: null, bonusOneValue: 0, bonusEveryValue: 0 };

/**
 * The library a room is playing (rooms from before libraries existed play Bollywood)
 * @param {Object} room     -   The room
 * @returns {Object}        -   Library
 */
export const getRoomLibrary = (room) => room?.library || bollywoodLibrary;

/**
 * The library's category attributes, each with its distinct values
 * @param {Object} library  -   Library
 * @returns {Array}         -   [{ name, values: [...] }]
 */
export const getCategoryAttributes = (library) =>
    library.attributes
        .filter((attribute) => attribute.kind === "category")
        .map((attribute) => ({
            name    :   attribute.name,
            values  :   [...new Set(library.items.map((item) => item.values?.[attribute.name]).filter((value) => value !== "" && value !== undefined))],
        }));

/**
 * Default constraints and bonuses for each category attribute of a library
 * @param {Object} library  -   Library
 * @returns {Object}        -   { [attribute]: { minDifferent, maxPerValue, bonusOneValue, bonusEveryValue } }
 */
export const getDefaultAttributeRules = (library) => {
    // A room's copy of a built-in library doesn't carry its rules, so look them up
    const defaultRules = library.defaultRules || builtInLibraries.find((builtIn) => builtIn.code === library.code)?.defaultRules || {};
    return Object.fromEntries(
        getCategoryAttributes(library).map(({ name }) => [name, { ...emptyRule, ...(defaultRules[name] || {}) }])
    );
};

/**
 * Sold items on a team. Old rooms stored Bollywood movies as { movieName, rating, actor, genre }.
 * @param {Object} team     -   Team
 * @returns {Array}         -   [{ name, score, values, soldPrice, soldAt }]
 */
export const getTeamItems = (team) =>
    (team?.items || team?.movies || []).map((item) =>
        item.name !== undefined ? item : {
            name        :   item.movieName,
            score       :   item.rating,
            values      :   { Actor: item.actor, Genre: item.genre },
            soldPrice   :   item.soldPrice,
            soldAt      :   item.soldAt,
        }
    );

/**
 * Poster for an item (only the built-in Bollywood library has them)
 * @returns {String|null}   -   Image, or null
 */
export const getItemPoster = (library, item) =>
    library.code === BOLLYWOOD_CODE && item ? getPosterImage(item.name) : null;

/**
 * The library fields that get copied onto a room
 * @param {Object} library  -   Library
 * @returns {Object}        -   Library for the room
 */
export const libraryForRoom = (library) => JSON.parse(JSON.stringify({
    // Copied as plain JSON: Firestore only accepts plain objects and arrays
    code            :   library.code,
    name            :   library.name,
    description     :   library.description || "",
    nameAttribute   :   library.nameAttribute,
    scoreAttribute  :   library.scoreAttribute,
    attributes      :   library.attributes,
    items           :   library.items,
    itemCount       :   library.itemCount ?? library.items.length,
}));
